import React from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Eye, 
  CloudRain, 
  Terminal, 
  Activity, 
  Globe, 
  ShieldAlert,
  HardDrive,
  RefreshCw,
  CheckCircle2
} from 'lucide-react';
import './SecureReliable.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Zero Trust Architecture',
    description: 'Never trust, always verify. We implement micro-segmentation and strict identity controls across every layer of your infrastructure.',
    features: [
      'Multi-factor authentication (MFA)',
      'Identity-aware proxying',
      'Least-privileged access'
    ],
    image: '/images/secure-reliable/zero-trust.jpg'
  },
  {
    title: 'Disaster Recovery',
    description: 'Business continuity is non-negotiable. We build automated failover systems that ensure your services stay online even in catastrophic failures.',
    features: [
      'Multi-region replication',
      'Point-in-time recovery (PITR)',
      'Automated health failover'
    ],
    image: '/images/secure-reliable/disaster-recovery.jpg'
  },
  {
    title: 'Threat Detection',
    description: 'Stop attackers before they start. Real-time monitoring and AI-driven analysis of logs to identify and mitigate anomalies.',
    features: [
      'SIEM/SOAR integration',
      'Intrusion detection (IDS/IPS)',
      'Vulnerability scanning'
    ],
    image: '/images/secure-reliable/threat-detection.jpg'
  }
];

const capabilities = [
  {
    icon: <Lock size={24} />,
    title: 'Encryption at Rest & Transit',
    text: 'All data is protected using AES-256 for storage and TLS 1.3 for communications, ensuring end-to-end privacy.'
  },
  {
    icon: <Eye size={24} />,
    title: 'Observability',
    text: 'Deep insights into your stack with centralized logging, distributed tracing, and real-time dashboarding.'
  },
  {
    icon: <ShieldAlert size={24} />,
    title: 'WAF & DDoS Protection',
    text: 'Shield your applications from web exploits and massive botnet attacks at the edge of the network.'
  },
  {
    icon: <Globe size={24} />,
    title: 'Compliance-Ready',
    text: 'Simplified auditing for GDPR, HIPAA, and SOC2 with automated compliance tracking and reporting.'
  }
];

const faqItems = [
  {
    question: 'How do you ensure data is never lost?',
    answer: 'We utilize a multi-layered backup strategy. Data is replicated across multiple physical locations in real-time. We also maintain periodic snapshots that are stored in immutable storage, preventing accidental or malicious deletion.'
  },
  {
    question: 'What is your uptime guarantee?',
    answer: 'For business-critical services, we design architectures that meet 99.99% uptime (High Availability). This is achieved through redundant load balancing, health checks, and automatic multi-zone failover.'
  },
  {
    question: 'Do you handle security patches?',
    answer: 'Yes. We implement automated patch management systems that update your operating systems and dependencies without downtime, ensuring you are protected against the latest vulnerabilities.'
  }
];

export default function SecureReliable() {
  return (
    <article className="secure-page">
      <Hero 
        eyebrow="Resilient Infrastructure"
        title="Secure by Design. Reliable by Nature."
        subtitle="Protect your most valuable assets with enterprise-grade security and automated recovery systems that ensure 24/7 business continuity."
        stats={[
          { label: 'Uptime', value: '99.99%' },
          { label: 'Recovery', value: '< 15m' }
        ]}
      />

      <section className="section section-capabilities">
        <div className="section-heading">
          <span className="eyebrow">Enterprise Security</span>
          <h2>A fortress for your digital operations.</h2>
          <p>We do not just add security; we bake it into every line of configuration and code to protect your users and data.</p>
        </div>
        <div className="capabilities-grid">
          {capabilities.map((cap, i) => (
            <div key={i} className="capability-card security">
              <div className="cap-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-solutions">
        <div className="solutions-container">
          {services.map((service, index) => (
            <div key={index} className={`solution-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="solution-copy">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((f, fi) => (
                    <li key={fi}><CheckCircle2 size={18} /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-media">
                <img src={service.image} alt={service.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <strong>AES-256</strong>
            <span>Strongest Encryption</span>
          </div>
          <div className="metric-item">
            <strong>0ms</strong>
            <span>Failover Lag</span>
          </div>
          <div className="metric-item">
            <strong>PCI/SOC2</strong>
            <span>Compliance Ready</span>
          </div>
          <div className="metric-item">
            <strong>24/7</strong>
            <span>Global Monitoring</span>
          </div>
        </div>
      </section>

      <section className="section section-faq">
        <div className="section-heading">
          <span className="eyebrow">Security Deep Dive</span>
          <h2>Reliability & Trust.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      
      <Footer />
    </article>
  );
}
